import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { RepositoryState, Vehicle, VehicleRepository } from '../data/vehicles'
import { DemoVehicleRepository } from '../data/vehicles'
import { Button } from '../ui/Button'
import { InventoryFilters, emptyInventoryFilters, type InventoryFilterState } from '../components/inventory/InventoryFilters'
import { VehicleCard } from '../components/inventory/VehicleCard'
import { useInventoryTranslation } from '../components/inventory/useInventoryTranslation'
import { useRouteMetadata } from '../components/inventory/useRouteMetadata'
import { PageHero } from '../components/page-hero/PageHero'
import '../components/inventory/inventory.css'

type InventorySort = 'source' | 'make-asc' | 'make-desc'

const defaultRepository = new DemoVehicleRepository()

export function applyVehicleQuery(
  vehicles: readonly Vehicle[],
  filters: InventoryFilterState,
  sort: InventorySort,
  locale: string,
) {
  const result = vehicles.filter((vehicle) => (
    (!filters.make || vehicle.make === filters.make)
    && (!filters.energy || vehicle.facts.energy === filters.energy)
    && (!filters.transmission || vehicle.facts.transmission === filters.transmission)
  ))

  if (sort === 'source') return result
  return [...result].sort((first, second) => {
    const comparison = first.make.localeCompare(second.make, locale)
    return sort === 'make-asc' ? comparison : -comparison
  })
}

export function InventoryPage({ repository = defaultRepository }: { repository?: VehicleRepository }) {
  const { language, t } = useInventoryTranslation()
  const { pathname } = useLocation()
  const [state, setState] = useState<RepositoryState<readonly Vehicle[]>>({ status: 'loading' })
  const [request, setRequest] = useState(0)
  const [filters, setFilters] = useState<InventoryFilterState>(emptyInventoryFilters)
  const [sort, setSort] = useState<InventorySort>('source')

  useRouteMetadata({
    title: t('catalogMetadataTitle'),
    description: t('catalogDescription'),
    pathname,
    language,
  })

  useEffect(() => {
    let active = true
    setState({ status: 'loading' })
    repository.list().then((result) => {
      if (active) setState(result)
    }).catch((error: unknown) => {
      if (active) setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
    })
    return () => {
      active = false
    }
  }, [repository, request])

  const vehicles = state.status === 'ready' ? state.data : []
  const visibleVehicles = useMemo(
    () => applyVehicleQuery(vehicles, filters, sort, language),
    [filters, language, sort, vehicles],
  )
  const countKey = visibleVehicles.length === 1 ? 'resultCount_one' : 'resultCount_other'
  return (
    <div className="inventory-page">
      <PageHero variant="inventory" eyebrow={t('catalogEyebrow')} title={t('catalogTitle')}>
        <p>{t('catalogIntro')}</p>
      </PageHero>

      <section className="inventory-catalog" aria-label={t('catalogTitle')}>
        <div className="page-frame inventory-catalog__layout">
          {state.status === 'ready' ? (
            <InventoryFilters
              vehicles={state.data}
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(emptyInventoryFilters)}
            />
          ) : null}

          <div className="inventory-results" aria-live="polite" aria-busy={state.status === 'loading'}>
            {state.status === 'ready' ? (
              <div className="inventory-results__toolbar">
                <p className="inventory-results__count">
                  {t(countKey, { count: visibleVehicles.length })}
                </p>
                <label className="inventory-sort">
                  <span>{t('sortLabel')}</span>
                  <select value={sort} onChange={(event) => setSort(event.target.value as InventorySort)}>
                    <option value="source">{t('sortSource')}</option>
                    <option value="make-asc">{t('sortMakeAsc')}</option>
                    <option value="make-desc">{t('sortMakeDesc')}</option>
                  </select>
                </label>
              </div>
            ) : null}

            {state.status === 'loading' ? (
              <div className="inventory-state inventory-state--loading" role="status">
                <span className="inventory-state__line" aria-hidden="true" />
                <p>{t('loading')}</p>
                <div className="inventory-skeleton-grid" aria-hidden="true">
                  {[0, 1, 2].map((item) => <span key={item} />)}
                </div>
              </div>
            ) : null}

            {state.status === 'error' ? (
              <div className="inventory-state" role="alert">
                <h2>{t('errorTitle')}</h2>
                <p>{t('errorBody')}</p>
                <Button type="button" variant="secondary" onClick={() => setRequest((value) => value + 1)}>
                  {t('retry')}
                </Button>
              </div>
            ) : null}

            {state.status === 'empty' || (state.status === 'ready' && !visibleVehicles.length) ? (
              <div className="inventory-state">
                <h2>{t('emptyTitle')}</h2>
                <p>{t('emptyBody')}</p>
                <Button type="button" variant="secondary" onClick={() => setFilters(emptyInventoryFilters)}>
                  {t('emptyAction')}
                </Button>
              </div>
            ) : null}

            {state.status === 'ready' && visibleVehicles.length ? (
              <div className="inventory-grid">
                {visibleVehicles.map((vehicle) => <VehicleCard key={vehicle.id} vehicle={vehicle} />)}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  )
}
